import { useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Cached page data (stale-while-revalidate).
 *
 * Drop-in replacement for the old `useEffect(fetch)` + `useState` pattern.
 * The first visit to a page fetches and shows its loading gate as before —
 * but the result is kept in the shared query cache, so returning to the page
 * renders the cached copy instantly while a background refresh runs. The
 * user never stares at "Loading…" for data the app already had.
 *
 * `key` must uniquely identify the data (include any fetch params in it).
 */
export function usePageData<T>(key: readonly unknown[], fetcher: () => Promise<T>) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: key, queryFn: fetcher });
  return {
    data: query.data,
    /** true only while there's no cached copy yet (very first visit) */
    loading: query.isLoading,
    error: query.isError,
    /** background refresh — cached data stays on screen while it runs */
    refetch: query.refetch,
    /** edit the cached copy in place (e.g. reflect a save without a refetch) */
    setData: (updater: (prev: T) => T) =>
      queryClient.setQueryData<T>(key as unknown[], (prev) =>
        prev === undefined ? prev : updater(prev),
      ),
  };
}
